import { type EntryPointRouteObject } from "@loop-payments/react-router-relay"
import React from "react"
import { Navigate, useParams } from "react-router"

import ChatsPage from "./pages/ChatsPage"
import { EmptyChats,LlmConversationChat } from "./pages/ChatsPage/routes"
import ContextEnginePage from "./pages/ContextEnginePage"
import { LegacyMemoryRedirect } from "./pages/ContextEnginePage/routes/snippetsRedirect"
import { DagvestigationDetails, DagvestigationsEmpty, DagvestigationsPage } from "./pages/Dagvestigations"
import InteractionRedirect from "./pages/InteractionPage/InteractionRedirect"
import KnowledgePage from "./pages/KnowledgePage"
import SkillsPage from "./pages/SkillsPage"
import WelcomePage from "./pages/WelcomePage"

// Redirect component for /chats/c/:conversationId -> /chat/c/:conversationId
const ChatsConversationRedirect: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  return <Navigate to={`/chat/c/${conversationId}`} replace />;
};

// Lazy load main pages
const DigestPage = React.lazy(() =>
  import("./pages/DigestPage").then((module) => {
    return { default: module.default };
  })
);
const CapabilitiesPage = React.lazy(() => import("./pages/CapabilitiesPage"));
const AlertRespondersPage = React.lazy(() => import("./pages/AlertRespondersPage"));
const CICDAgentsPage = React.lazy(() => import("./pages/CICDAgentsPage"));
const AutopilotPage = React.lazy(() => import("./pages/AutopilotPage"));
const SupportAgentsPage = React.lazy(() => import("./pages/SupportAgentsPage"));
const PullRequestsPage = React.lazy(() => import("./pages/PullRequestsPage"));
const TierzeroMcpPage = React.lazy(() => import("./pages/TierzeroMcpPage"));
const IncidentResponsePage = React.lazy(() => import("./pages/IncidentResponsePage"));
const OncallHandoffPage = React.lazy(() => import("./pages/OncallHandoffPage"));
const ServiceMapPage = React.lazy(() => import("./pages/ServiceMapPage"));
const StudioPage = React.lazy(() => import("./pages/StudioPage/index-v2"));
const InternPage = React.lazy(() => import("./pages/intern/InternPage"));
// Temporarily import directly to test
import SettingsPageImport from "./pages/SettingsPage";
const SettingsPage = SettingsPageImport;
// Keep nested components as direct imports since they're smaller
import { CapabilitiesWorkspace } from "./pages/CapabilitiesPage/components/CapabilitiesWorkspace";
import { CapabilityDetail } from "./pages/CapabilitiesPage/components/CapabilityDetail";
import DigestDetails from "./pages/DigestPage/DigestDetails";
import DigestJobDetailsPage from "./pages/DigestPage/DigestJobDetailsPage";
import DigestJobs from "./pages/DigestPage/DigestJobs";
import ExtensionRefreshPage from "./pages/ExtensionRefreshPage"
import OnboardingPage from "./pages/OnboardingPage"
import { IntegrationCategoryRoute,OnboardingMainRoute } from "./pages/OnboardingPage/routes"
import { AuditLogsRoute, BillingRoute, CreateMcpServerRoute, CreateTeamRoute, EditMcpServerRoute, EditTeamRoute, IntegrationsRoute, SettingsIntegrationCategoryRoute, TeamsRoute } from "./pages/SettingsPage/routes"
import SettingsDefaultRedirect from "./pages/SettingsPage/routes/SettingsDefaultRedirect"
import SlackConnectPage from "./pages/SlackConnectPage"
import SuspendedPage from "./pages/SuspendedPage";

export const APP_ROUTES: EntryPointRouteObject[] = [
  {
    path: "/",
    Component: WelcomePage,
  },
  {
    path: "/suspended",
    Component: SuspendedPage,
  },
  {
    path: "/chat",
    Component: ChatsPage,
    children: [
      {
        index: true,
        Component: EmptyChats
      },
      {
        path: "c/:conversationId",
        Component: LlmConversationChat
      }
    ]
  },
  {
    path: "/pull-requests",
    Component: PullRequestsPage,
    children: [
      {
        index: true,
        lazy: () =>
          import("./pages/PullRequestsPage/PullRequestsList").then((m) => ({
            Component: m.PullRequestsList,
          })),
      },
    ],
  },
  {
    path: "/mcp",
    Component: TierzeroMcpPage,
  },
  {
    path: "/chats",
    element: <Navigate to="/chat" replace />,
  },
  {
    path: "/chats/c/:conversationId",
    Component: ChatsConversationRedirect,
  },
  {
    path: "/interactions/:id",
    Component: InteractionRedirect,
  },
  {
    path: "/digests",
    Component: DigestPage,
    children: [
      {
        index: true,
        Component: DigestJobs
      },
      {
        path: "job/:jobId",
        Component: DigestJobDetailsPage
      },
      {
        path: "run/:digestJobId",
        Component: DigestDetails
      }
    ]
  },
  {
    path: "/investigations",
    Component: DagvestigationsPage,
    children: [
      {
        index: true,
        Component: DagvestigationsEmpty
      },
      {
        path: ":jobRunId",
        Component: DagvestigationDetails
      }
    ]
  },
  {
    path: "/alerts",
    Component: AlertRespondersPage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/AlertRespondersPage").then(m => ({ Component: m.AlertRespondersPageContent })),
      },
      {
        path: "new/:alertId",
        lazy: async () => {
          const [{ default: AlertResponderDetailsV2 }, { RequireSuperAdmin }] = await Promise.all([
            import("./pages/AlertRespondersPage/AlertResponderDetailsV2"),
            import("./components/RequireSuperAdmin"),
          ]);
          return {
            Component: () => (
              <RequireSuperAdmin redirectTo="/alerts">
                <AlertResponderDetailsV2 />
              </RequireSuperAdmin>
            ),
          };
        },
      },
      {
        path: ":alertId",
        lazy: () => import("./pages/AlertRespondersPage/AlertResponderDetailsSwitch").then(m => ({ Component: m.default })),
      },
    ],
  },
  {
    path: "/incidents",
    Component: IncidentResponsePage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/IncidentResponsePage/IncidentInsights").then(m => ({ Component: m.default })),
      },
      {
        path: "list",
        lazy: () => import("./pages/IncidentResponsePage/IncidentsList").then(m => ({ Component: m.default })),
      },
      {
        path: "settings",
        lazy: () => import("./pages/IncidentResponsePage/IncidentResponseSettings").then(m => ({ Component: m.default })),
      },
      {
        path: "start",
        lazy: () => import("./pages/IncidentResponsePage/IncidentResponseStart").then(m => ({ Component: m.default })),
      },
      {
        path: ":incidentId",
        lazy: () => import("./pages/IncidentResponsePage/IncidentDetail").then(m => ({ Component: m.default })),
      },
    ],
  },
  {
    path: "/support/agents",
    Component: SupportAgentsPage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/SupportAgentsPage").then(m => ({ Component: m.SupportAgentsPageContent })),
      },
      {
        path: ":agentId",
        lazy: () => import("./pages/SupportAgentsPage/SupportAgentDetails").then(m => ({ Component: m.SupportAgentDetails })),
      },
    ],
  },
  {
    path: "/cicd",
    Component: CICDAgentsPage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/CICDAgentsPage").then(m => ({ Component: m.CICDAgentsPageContent })),
      },
      {
        path: ":agentId",
        lazy: () => import("./pages/CICDAgentsPage/CICDAgentDetails").then(m => ({ Component: m.default })),
      },
    ],
  },
  {
    path: "/oncall",
    Component: OncallHandoffPage,
  },
  {
    path: "/autopilot",
    Component: AutopilotPage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/AutopilotPage").then(m => ({ Component: m.AutopilotPageContent })),
      },
      {
        path: "new",
        lazy: () => import("./pages/AutopilotPage/CreateOptimizerPage").then(m => ({ Component: m.CreateOptimizerPage })),
      },
      {
        path: ":optimizerId",
        lazy: () => import("./pages/AutopilotPage/OptimizerDetails").then(m => ({ Component: m.default })),
      },
      {
        path: ":optimizerId/issues/:issueId",
        lazy: () => import("./pages/AutopilotPage/OptimizerIssuePage").then(m => ({ Component: m.default })),
      },
    ],
  },
  {
    path: "/knowledge",
    Component: KnowledgePage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/KnowledgePage/routes/KnowledgeDefaultRedirect").then(m => ({ Component: m.default })),
      },
      {
        // eslint-disable-next-line custom/no-hardcoded-brand-name -- route path
        path: "tierzero.md",
        lazy: () => import("./pages/KnowledgePage/routes/TierZeroRoute").then(m => ({ Component: m.default })),
      },
      {
        path: "agent.md",
        lazy: () => import("./pages/KnowledgePage/routes/TierZeroRoute").then(m => ({ Component: m.default })),
      },
      {
        path: "integrations",
        element: <Navigate to="/context/resources" replace />,
      },
      {
        path: "integrations/:integrationType/:catalogType.json",
        element: <Navigate to="/context/resources" replace />,
      },
      {
        path: "memory",
        element: <LegacyMemoryRedirect />,
      }
    ]
  },
  {
    path: "/context",
    Component: ContextEnginePage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/ContextEnginePage/routes/ContextEngineDefaultRedirect").then(m => ({ Component: m.default })),
      },
      {
        path: "snippets",
        lazy: () => import("./pages/ContextEnginePage/routes/ContextSnippetsRoute").then(m => ({ Component: m.default })),
      },
      {
        path: "resources",
        lazy: () => import("./pages/ContextEnginePage/routes/ContextResourcesRoute").then(m => ({ Component: m.default })),
      },
    ]
  },
  {
    path: "/capabilities",
    Component: CapabilitiesPage,
    children: [
      {
        index: true,
        Component: CapabilitiesWorkspace,
      },
      {
        path: ":capabilityId",
        Component: CapabilityDetail,
      },
    ],
  },
  {
    path: "/skills",
    Component: SkillsPage,
    children: [
      {
        index: true,
        lazy: () => import("./pages/SkillsPage/components/SkillsWorkspace").then(m => ({ Component: m.SkillsWorkspace })),
      },
    ],
  },
  {
    path: "/service-intelligence",
    Component: ServiceMapPage,
    children: [
      { path: "n/:nodeSlug", Component: ServiceMapPage },
    ],
  },
  {
    path: "/studio",
    Component: StudioPage,
  },
  {
    path: "/onboarding",
    Component: OnboardingPage,
    children: [
      {
        index: true,
        Component: OnboardingMainRoute,
      },
      {
        path: "integrations/:categorySlug",
        Component: IntegrationCategoryRoute,
      },
      // Legacy redirects
      {
        path: "1",
        element: <Navigate to="/onboarding" replace />,
      },
      {
        path: "2",
        element: <Navigate to="/onboarding" replace />,
      },
      {
        path: "3",
        element: <Navigate to="/onboarding" replace />,
      },
      {
        path: "4",
        element: <Navigate to="/onboarding" replace />,
      },
    ],
  },
  {
    path: "/connect/slack/:token",
    Component: SlackConnectPage,
  },
  {
    path: "/settings",
    Component: SettingsPage,
    children: [
      {
        index: true,
        Component: SettingsDefaultRedirect,
      },
      {
        path: "integrations",
        Component: IntegrationsRoute,
      },
      {
        path: "integrations/mcp-servers/new",
        Component: CreateMcpServerRoute,
      },
      {
        path: "integrations/mcp-servers/:serverId/edit",
        Component: EditMcpServerRoute,
      },
      {
        path: "integrations/:categorySlug",
        Component: SettingsIntegrationCategoryRoute,
      },
      {
        path: "admin",
        element: <Navigate to="/intern/orgs" replace />,
      },
      {
        path: "superadmin",
        element: <Navigate to="/intern/orgs" replace />,
      },
      {
        path: "teams",
        Component: TeamsRoute,
      },
      {
        path: "teams/new",
        Component: CreateTeamRoute,
      },
      {
        path: "teams/:teamId/edit",
        Component: EditTeamRoute,
      },
      {
        path: "network",
        lazy: () => import("./pages/SettingsPage/routes/NetworkRoute").then(m => ({ Component: m.default })),
      },
      {
        path: "audit-logs",
        Component: AuditLogsRoute,
      },
      {
        path: "billing",
        Component: BillingRoute,
      },
      {
        path: "cost",
        lazy: () => import("./pages/SettingsPage/routes/CostV3Route").then(m => ({ Component: m.default })),
      },
      {
        path: "usage",
        lazy: () => import("./pages/SettingsPage/routes/UsageV2Route").then(m => ({ Component: m.default })),
      },
      {
        path: "value",
        lazy: () => import("./pages/SettingsPage/routes/ValueDashboardRoute").then(m => ({ Component: m.default })),
      },
      {
        path: "features",
        element: <Navigate to="/intern/orgs" replace />,
      },
      {
        path: "onboarding",
        element: <Navigate to="/onboarding" replace />,
      },
      {
        path: "onboarding/:step",
        element: <Navigate to="/onboarding" replace />,
      },
    ],
  },
  {
    path: "/intern",
    Component: InternPage,
    children: [
      {
        index: true,
        element: <Navigate to="/intern/orgs" replace />,
      },
      {
        path: "orgs",
        lazy: () => import("./pages/intern/InternOrgsView").then(m => ({ Component: m.default })),
      },
      {
        path: "orgs/:orgId",
        lazy: () => import("./pages/intern/InternOrgFlagsView").then(m => ({ Component: m.default })),
      },
      {
        path: "flags",
        lazy: () => import("./pages/intern/InternFlagsView").then(m => ({ Component: m.default })),
      },
      {
        path: "flags/:flagId",
        lazy: () => import("./pages/intern/InternFlagOrgsView").then(m => ({ Component: m.default })),
      },
      {
        path: "usage",
        lazy: () => import("./pages/intern/InternUsageView").then(m => ({ Component: m.default })),
      },
      {
        path: "cost",
        lazy: () => import("./pages/intern/InternCostView").then(m => ({ Component: m.default })),
      },
      {
        path: "fork",
        lazy: () => import("./pages/intern/InternForkView").then(m => ({ Component: m.default })),
      },
      {
        path: "memories",
        lazy: () => import("./pages/intern/InternWorkflowMemoriesView").then(m => ({ Component: m.default })),
      },
      {
        path: "memories/workflows/:workflowType/:workflowId",
        lazy: () => import("./pages/intern/InternWorkflowMemoryJobView").then(m => ({ Component: m.default })),
      },
      {
        path: "workflow-memory-eval",
        lazy: () => import("./pages/intern/InternWorkflowMemoryEvalView").then(m => ({ Component: m.default })),
      },
      {
        path: "workflow-memory-eval/:pairId",
        lazy: () =>
          import("./pages/intern/InternWorkflowMemoryShadowEvalDetail").then(m => ({ Component: m.default })),
      },
      {
        path: "workflow-memory-consolidation",
        lazy: () =>
          import("./pages/intern/InternWorkflowMemoryConsolidationList").then(m => ({ Component: m.default })),
      },
      {
        path: "workflow-memory-consolidation/:orgId/:digestJobId",
        lazy: () =>
          import("./pages/intern/InternWorkflowMemoryConsolidationDetail").then(m => ({ Component: m.default })),
      },
    ],
  },
  {
    path: "/extension/refresh",
    Component: ExtensionRefreshPage,
  }
];
